import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { mvpApi } from '../services/api';
import { getPathWithRef } from '../utils/attribution';
import { showPopupMessage } from '../utils/showPopup';
import LanguageSwitch from '../components/LanguageSwitch';
import './AgentConsole.css';

/**
 * Agent console: status, queue, active chats, assign, send messages. Mobile-first.
 */
export default function AgentConsole() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('offline');
  const [capacity, setCapacity] = useState(1);
  const [queue, setQueue] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');

  const refreshQueue = useCallback(async () => {
    try {
      const res = await mvpApi.getAgentQueue();
      setQueue(res.data?.queue || []);
    } catch (err) {
      console.error('Failed to refresh queue:', err);
    }
  }, []);

  const refreshActive = useCallback(async () => {
    try {
      const res = await mvpApi.getAgentActiveChats();
      const sessions = res.data?.sessions || [];
      setActiveSessions(sessions.map((s) => ({ ...s, id: s.session_id ?? s.id })));
    } catch (err) {
      console.error('Failed to refresh active:', err);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate(getPathWithRef('/'));
      return;
    }
    const load = async () => {
      try {
        const statusRes = await mvpApi.getAgentAvailability();
        setStatus(statusRes.data?.status || 'offline');
        setCapacity(statusRes.data?.capacity ?? 1);
        if (statusRes.data?.status === 'offline') {
          await mvpApi.setAgentAvailability('online', statusRes.data?.capacity || 1);
          setStatus('online');
        }
      } catch (err) {
        console.error('Failed to load agent status:', err);
      }
      await refreshQueue();
      await refreshActive();
    };
    load();
  }, [navigate, refreshQueue, refreshActive]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshQueue();
      refreshActive();
    }, status === 'online' ? 2000 : 15000);
    return () => clearInterval(interval);
  }, [status, refreshQueue, refreshActive]);

  useEffect(() => {
    if (!selectedSession) {
      setMessages([]);
      return;
    }
    const loadMessages = () => {
      mvpApi
        .getLiveChatMessages(selectedSession)
        .then((res) => setMessages(res.data?.messages || []))
        .catch(() => setMessages([]));
    };
    loadMessages();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedSession]);

  const handleSetAvailability = async () => {
    try {
      await mvpApi.setAgentAvailability(status, Number(capacity));
      await refreshQueue();
      await refreshActive();
    } catch (err) {
      showPopupMessage('Failed to update availability.', 'error');
    }
  };

  const assignSession = async (sessionId) => {
    try {
      await mvpApi.assignAgentSession(sessionId);
      setSelectedSession(sessionId);
      await mvpApi.getLiveChatMessages(sessionId).then((res) => setMessages(res.data?.messages || []));
      refreshQueue();
      refreshActive();
    } catch (err) {
      showPopupMessage('Failed to assign session.', 'error');
    }
  };

  const sendMessage = async () => {
    if (!selectedSession || !input.trim()) return;
    try {
      await mvpApi.sendAgentMessage(selectedSession, input.trim());
      setInput('');
      const res = await mvpApi.getLiveChatMessages(selectedSession);
      setMessages(res.data?.messages || []);
    } catch (err) {
      showPopupMessage('Failed to send message.', 'error');
    }
  };

  return (
    <div className="mvp-agent-page">
      <header className="mvp-agent-header">
        <button type="button" className="mvp-agent-back" onClick={() => navigate('/advisor')}>
          ← {t('back')}
        </button>
        <h1 className="mvp-agent-title">{t('admin.agentConsole')}</h1>
        <LanguageSwitch />
      </header>

      <main className="mvp-agent-main">
        <section className="mvp-agent-status">
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="online">Online</option>
            <option value="offline">Offline</option>
            <option value="busy">Busy</option>
          </select>
          <label>Capacity</label>
          <input
            type="number"
            min={1}
            max={10}
            value={capacity}
            onChange={(e) => setCapacity(Number(e.target.value))}
          />
          <button type="button" className="mvp-agent-btn" onClick={handleSetAvailability}>
            Update
          </button>
        </section>

        <section className="mvp-agent-section">
          <h2>Queue {queue.length > 0 && `(${queue.length})`}</h2>
          {queue.length === 0 && <p className="mvp-agent-muted">No queued sessions.</p>}
          {queue.map((q) => (
            <div key={q.session_id} className="mvp-agent-card">
              <span>Session #{q.session_id} · User {q.user_id}</span>
              <button type="button" className="mvp-agent-assign" onClick={() => assignSession(q.session_id)}>
                Assign to me
              </button>
            </div>
          ))}
        </section>

        <section className="mvp-agent-section">
          <h2>{t('admin.agent.activeSessions')}</h2>
          {activeSessions.length === 0 && <p className="mvp-agent-muted">No active sessions.</p>}
          {activeSessions.map((s) => {
            const sid = s.session_id ?? s.id;
            const isSelected = selectedSession === sid;
            return (
              <div key={sid} className="mvp-agent-card">
                <span className="mvp-agent-session-info">
                  Session #{String(sid).slice(0, 8)}… {s.user_id != null && `· User ${s.user_id}`}
                </span>
                <button
                  type="button"
                  className={isSelected ? 'mvp-agent-btn-close' : 'mvp-agent-btn-open'}
                  onClick={() => setSelectedSession(isSelected ? null : sid)}
                >
                  {isSelected ? t('admin.agent.closeChat') : t('admin.agent.openChat')}
                </button>
              </div>
            );
          })}
        </section>

        {selectedSession && (
          <section className="mvp-agent-chat-panel">
            <div className="mvp-agent-chat-header">
              <h2>{t('admin.agent.talkToUser')}</h2>
              <span className="mvp-agent-chat-session">Session #{String(selectedSession).slice(0, 12)}…</span>
              <button
                type="button"
                className="mvp-agent-btn-close-inline"
                onClick={() => setSelectedSession(null)}
              >
                {t('admin.agent.closeChat')}
              </button>
            </div>
            <div className="mvp-agent-messages">
              {messages.length === 0 && <p className="mvp-agent-muted">No messages yet. Say hello to the user.</p>}
              {messages.map((m, i) => (
                <div key={i} className={`mvp-agent-msg mvp-agent-msg--${m.sender_type}`}>
                  <span className="mvp-agent-msg-label">{m.sender_type === 'agent' ? 'You' : 'User'}</span>
                  {m.content}
                </div>
              ))}
            </div>
            <div className="mvp-agent-send">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t('admin.agent.placeholder')}
                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
              />
              <button type="button" className="mvp-agent-send-btn" onClick={sendMessage}>
                {t('admin.agent.send')}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
